import React, { useState, useRef, useEffect } from 'react';
import { Check, RotateCcw } from 'lucide-react';
import { Button } from './Button';
import { cn } from '../../lib/utils';

interface ColorPickerProps {
  defaultColor: string;
  onChange: (color: string) => void;
  onSave: (color: string) => void;
}

export const ColorPicker: React.FC<ColorPickerProps> = ({
  defaultColor,
  onChange,
  onSave,
}) => {
  const [color, setColor] = useState(defaultColor || '#0a8345');
  const [hue, setHue] = useState(0);
  const [saturation, setSaturation] = useState(100);
  const [lightness, setLightness] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [hexInput, setHexInput] = useState('');

  const saturationRef = useRef<HTMLDivElement>(null);
  const hueRef = useRef<HTMLDivElement>(null);

  console.log('🎨 ColorPicker rendu avec:', { defaultColor, currentColor: color });

  // Fonction pour convertir hex en HSL
  const hexToHsl = (hex: string): [number, number, number] => {
    if (!hex || !hex.startsWith('#')) {
      console.warn('⚠️ Couleur hex invalide:', hex);
      return [0, 100, 50];
    }
    
    const r = parseInt(hex.slice(1, 3), 16) / 255;
    const g = parseInt(hex.slice(3, 5), 16) / 255;
    const b = parseInt(hex.slice(5, 7), 16) / 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h = 0;
    let s = 0;
    const l = (max + min) / 2;

    if (max !== min) {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r: h = (g - b) / d + (g < b ? 6 : 0); break;
        case g: h = (b - r) / d + 2; break;
        case b: h = (r - g) / d + 4; break;
      }
      h /= 6;
    }

    return [h * 360, s * 100, l * 100];
  };

  // Fonction pour convertir HSL en RGB
  const hslToRgb = (h: number, s: number, l: number): [number, number, number] => {
    s /= 100;
    l /= 100;
    const k = (n: number) => (n + h / 30) % 12;
    const a = s * Math.min(l, 1 - l);
    const f = (n: number) =>
      l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
    return [
      Math.round(255 * f(0)),
      Math.round(255 * f(8)),
      Math.round(255 * f(4)),
    ];
  };

  // Fonction pour convertir RGB en hex
  const rgbToHex = (r: number, g: number, b: number): string => {
    return '#' + [r, g, b].map(x => {
      const hex = x.toString(16);
      return hex.length === 1 ? '0' + hex : hex;
    }).join('');
  };

  // Initialiser les valeurs HSL depuis defaultColor une seule fois
  useEffect(() => {
    console.log('🔄 ColorPicker useEffect - Initialisation avec defaultColor:', defaultColor);
    
    if (defaultColor && defaultColor !== color) {
      const [h, s, l] = hexToHsl(defaultColor);
      console.log('🎨 Conversion HSL:', { hex: defaultColor, h, s, l });
      setHue(h);
      setSaturation(s);
      setLightness(l);
      setColor(defaultColor);
      setHexInput(defaultColor);
      console.log('✅ ColorPicker initialisé avec:', defaultColor);
    }
  }, [defaultColor]); // Seulement quand defaultColor change

  // Mettre à jour la couleur quand HSL change (mais pas pendant l'initialisation)
  const updateColorFromHSL = () => {
    const [r, g, b] = hslToRgb(hue, saturation, lightness);
    const hex = rgbToHex(r, g, b);
    
    console.log('🔄 Mise à jour couleur HSL -> HEX:', { h: hue, s: saturation, l: lightness, hex });
    
    if (hex !== color) {
      setColor(hex);
      setHexInput(hex);
      console.log('📡 Appel onChange avec nouvelle couleur:', hex);
      onChange(hex);
    }
  };

  // Gérer le clic sur le sélecteur de saturation/luminosité
  const handleSaturationMouseDown = (e: React.MouseEvent) => {
    if (!saturationRef.current) return;
    
    const rect = saturationRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const y = Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height));
    
    console.log('🎨 Clic saturation/luminosité:', { x, y });
    
    const newSaturation = x * 100;
    const newLightness = 100 - y * 100;
    
    setSaturation(newSaturation);
    setLightness(newLightness);
    setIsDragging(true);
    
    console.log('🎨 Nouvelles valeurs S/L:', { saturation: newSaturation, lightness: newLightness });
    
    // Mettre à jour immédiatement la couleur
    setTimeout(updateColorFromHSL, 0);
  };

  // Gérer le clic sur le sélecteur de teinte
  const handleHueMouseDown = (e: React.MouseEvent) => {
    if (!hueRef.current) return;
    
    const rect = hueRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    
    console.log('🎨 Clic teinte:', { x });
    
    const newHue = x * 360;
    setHue(newHue);
    setIsDragging(true);
    
    console.log('🎨 Nouvelle teinte:', newHue);
    
    // Mettre à jour immédiatement la couleur
    setTimeout(updateColorFromHSL, 0);
  };

  // Gérer le mouvement de la souris
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging) return;
      
      if (saturationRef.current) {
        const rect = saturationRef.current.getBoundingClientRect();
        const x = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
        const y = Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height));
        setSaturation(x * 100);
        setLightness(100 - y * 100);
        updateColorFromHSL();
      }
      
      if (hueRef.current) {
        const rect = hueRef.current.getBoundingClientRect();
        const x = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
        setHue(x * 360);
        updateColorFromHSL();
      }
    };

    const handleMouseUp = () => {
      console.log('🎨 Fin de glissement');
      setIsDragging(false);
    };

    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, hue, saturation, lightness]);

  // Gérer la sauvegarde
  const handleSave = () => {
    console.log('💾 ColorPicker - Sauvegarde demandée pour:', color);
    onSave(color);
    setShowConfirmation(true);
    setTimeout(() => setShowConfirmation(false), 2000);
  };

  // Gérer la réinitialisation
  const handleReset = () => {
    console.log('🔄 ColorPicker - Reset vers:', defaultColor);
    const [h, s, l] = hexToHsl(defaultColor);
    setHue(h);
    setSaturation(s);
    setLightness(l);
    setColor(defaultColor);
    setHexInput(defaultColor);
    onChange(defaultColor);
  };

  // Gérer la saisie manuelle du code hex
  const handleHexInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setHexInput(value);
    
    // Valider le format hex
    const hexRegex = /^#[0-9A-Fa-f]{6}$/;
    if (hexRegex.test(value)) {
      const [h, s, l] = hexToHsl(value);
      setHue(h);
      setSaturation(s);
      setLightness(l);
      setColor(value);
      onChange(value);
    }
  };

  return (
    <div className="space-y-6 sm:space-y-4">
      {/* Aperçu de la couleur */}
      <div className="flex items-center gap-4 sm:gap-3">
        <div
          className="h-20 w-20 sm:h-16 sm:w-16 rounded-lg shadow-inner"
          style={{ backgroundColor: color }}
        />
        <div>
          <p className="font-medium text-base sm:text-sm text-gray-900 dark:text-white">
            {color.toUpperCase()}
          </p>
          <p className="text-base sm:text-sm text-gray-500 dark:text-gray-400">
            {`RGB(${hslToRgb(hue, saturation, lightness).join(', ')})`}
          </p>
        </div>
      </div>

      {/* Saisie manuelle du code couleur */}
      <div>
        <label className="block text-base sm:text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
          Code couleur
        </label>
        <input
          type="text"
          value={hexInput}
          onChange={handleHexInputChange}
          placeholder="#0b8043"
          className="w-full font-mono text-base sm:text-sm touch-manipulation"
          pattern="^#[0-9A-Fa-f]{6}$"
          title="Format: #RRGGBB (ex: #0b8043)"
        />
        <p className="mt-1 text-sm sm:text-xs text-gray-500 dark:text-gray-400">
          Format: #RRGGBB (ex: #0b8043)
        </p>
      </div>

      {/* Sélecteur de saturation/luminosité */}
      <div
        ref={saturationRef}
        className="relative h-56 sm:h-48 w-full cursor-crosshair rounded-lg touch-manipulation"
        style={{
          backgroundColor: `hsl(${hue}, 100%, 50%)`,
        }}
        onMouseDown={handleSaturationMouseDown}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-white to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black" />
        <div
          className="absolute h-4 w-4 sm:h-3 sm:w-3 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white shadow-sm"
          style={{
            left: `${saturation}%`,
            top: `${100 - lightness}%`,
          }}
        />
      </div>

      {/* Sélecteur de teinte */}
      <div
        ref={hueRef}
        className="relative h-6 sm:h-4 w-full cursor-pointer rounded-lg touch-manipulation"
        style={{
          background:
            'linear-gradient(to right, #ff0000 0%, #ffff00 17%, #00ff00 33%, #00ffff 50%, #0000ff 67%, #ff00ff 83%, #ff0000 100%)',
        }}
        onMouseDown={handleHueMouseDown}
      >
        <div
          className="absolute h-full w-1.5 sm:w-1 -translate-x-1/2 rounded-sm border border-white bg-white shadow-sm"
          style={{ left: `${(hue / 360) * 100}%` }}
        />
      </div>

      {/* Actions */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 sm:gap-0">
        <Button
          variant="secondary"
          onClick={handleReset}
          className="flex items-center gap-2"
        >
          <RotateCcw className="h-4 w-4" />
          Réinitialiser
        </Button>
        <div className="flex items-center gap-2">
          {showConfirmation && (
            <span className="text-base sm:text-sm text-green-500 dark:text-green-400">
              Couleur enregistrée !
            </span>
          )}
          <Button onClick={handleSave} className="flex items-center gap-2">
            <Check className="h-4 w-4" />
            Enregistrer
          </Button>
        </div>
      </div>
    </div>
  );
};